import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@copro/api';
import { AnalyzedContractResult } from '../types';

export type AnalyzeContractParams = {
  file: File;
  spaceId: string;
  providerName?: string;
  signal?: AbortSignal;
};

const DEFAULT_ERROR = "L'analyse du contrat a échoué. Vous pouvez remplir les informations manuellement.";

async function readErrorMessage(error: { message: string }): Promise<string> {
  const errorWithContext = error as unknown as { context?: unknown };
  if (errorWithContext.context instanceof Response) {
    try {
      const body = (await errorWithContext.context.clone().json()) as { error?: string; message?: string };
      if (body?.error) return body.error;
      if (body?.message) return body.message;
    } catch {
      // Fallback
    }
  }
  return error.message || DEFAULT_ERROR;
}

export async function analyzeContract(params: AnalyzeContractParams): Promise<AnalyzedContractResult> {
  if (params.signal?.aborted) {
    throw new DOMException('Operation aborted', 'AbortError');
  }

  const formData = new FormData();
  formData.append('file', params.file);
  formData.append('spaceId', params.spaceId);
  if (params.providerName) {
    formData.append('providerName', params.providerName);
  }

  const { data, error } = await apiClient.functions.invoke<{ data: AnalyzedContractResult }>('analyze-contract', {
    body: formData,
    signal: params.signal,
    headers: {},
  });

  if (params.signal?.aborted) {
    throw new DOMException('Operation aborted', 'AbortError');
  }

  if (error) {
    throw new Error(await readErrorMessage(error));
  }

  if (!data?.data) {
    throw new Error(DEFAULT_ERROR);
  }

  return data.data;
}

export function useAnalyzeContract() {
  return useMutation({
    mutationFn: analyzeContract,
  });
}
